import {ContactRelationship, School, SchoolClass} from "models"
import React, {useMemo, useRef} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import Checkbox from "components/chalky-layout/checkbox"
import classNames from "classnames"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {Form} from "@kaspernj/api-maker/build/form"
import Heading from "components/utils/heading"
import memo from "set-state-compare/build/memo"
import Modal from "components/modal"
import PropTypes from "prop-types"
import RadioButton from "components/utils/radio-button"
import TeachersSelect from "components/user/admin/school-classes/edit/teachers-select"
import Text from "@kaspernj/api-maker/build/utils/text"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import UtilsButton from "components/utils/button"
import UtilsInput from "components/utils/input"
import {v4 as uuidv4} from "uuid"
import {View} from "react-native"

/**
 * @typedef {object} ComponentsSchoolRolesNewRoleModalProps
 * @property {unknown} className
 * @property {unknown} onRequestClose
 * @property {unknown} school
 */

/**
 * @typedef {object} ComponentsSchoolRolesNewRoleModalState
 * @property {boolean} createNewUser
 * @property {unknown} schoolClasses
 * @property {unknown} schoolClassIds
 * @property {unknown} userId
 */

/**
 * @extends {ShapeComponent<ComponentsSchoolRolesNewRoleModalProps, ComponentsSchoolRolesNewRoleModalState>}
 */
class ComponentsSchoolRolesNewRoleModal extends ShapeComponent {
  /** @type {ComponentsSchoolRolesNewRoleModalState} */
  state = {
    schoolClasses: null,
    createNewUser: false,
    schoolClassIds: [],
    userId: undefined
  }

  static propTypes = {
    school: PropTypes.instanceOf(School).isRequired
  }

  setup() {
    const {school} = this.p
    const {locale, t} = useI18n({namespace: "js.components.school_roles.new_role_modal"})
    const schoolRole = useMemo(() => new ContactRelationship(), [])

    this.formRef = useRef()
    this.locale = locale
    this.schoolRole = schoolRole
    this.t = t


    useMemo(() => {
      this.loadSchoolClasses()
    }, [school.id()])
  }

  async loadSchoolClasses() {
    const schoolClasses = await SchoolClass
      .ransack({
        s: "current_translation_name",
        school_id_eq: this.p.school.id()
      })
      .select({SchoolClass: ["id", "name"]})
      .toArray()

    this.setState({schoolClasses})
  }

  render() {
    const {formRef, locale, schoolRole, t} = this.tt
    const {className, school, ...restProps} = this.props
    const {schoolClasses} = this.s
    const form = formRef.current
    const userFirstName = form?.getValue("school_role[user_attributes][first_name]")
    const userLastName = form?.getValue("school_role[user_attributes][last_name]")
    const userEmail = form?.getValue("school_role[user_attributes][email]")

    this.schoolRole = schoolRole


    const headerTitle = useMemo(() => {
      if (schoolRole?.isNewRecord()) {
        return t(".add_user_to_school")
      } else if (schoolRole?.isPersisted()) {
        return t(".edit_role_on_school")
      }
    }, [locale, schoolRole?.id()])

    return (
      <Modal className={classNames("components--school-roles--new-role-modal", className)} {...restProps}>
        <Form formObjectRef={this.tt.formRef}>
          <Heading>{headerTitle}</Heading>
          <Text style={this.roleOnSchoolTextStyle ||= {fontSize: 16, fontWeight: "bold"}}>
            {t(".role_on_school")}
          </Text>
          <View style={this.rolesContainerViewStyle ||= {marginBottom: 15}}>
            <RadioButton defaultChecked label={t(".teacher")} name="school_role[role]" testID="role-radio-button-teacher" value="teacher" />
            <RadioButton label={t(".school_leader")} name="school_role[role]" testID="role-radio-button-school-leader" value="school_leader" />
          </View>
          <Text style={this.assignToClassesTextStyle ||= {fontSize: 16, fontWeight: "bold"}}>
            {t(".assign_to_classes")}
          </Text>
          <View style={this.schoolClassesContainerViewStyle ||= {marginBottom: 15}}>
            {schoolClasses?.map((schoolClass) =>
              <View key={schoolClass.id()} style={this.schoolClassCheckboxViewStyle ||= {marginBottom: 10}}>
                <Checkbox label={schoolClass.name()} name="school_classes[]" testID={`school-class-checkbox-${schoolClass.id()}`} value={schoolClass.id()} />
              </View>
            )}
          </View>
          {school &&
            <TeachersSelect
              id="user_id"
              label={t(".assign_user")}
              onChange={this.tt.onUserChanged}
              school={school}
            />
          }
          {this.s.createNewUser &&
            <View style={this.inputsViewStyle ||= {marginTop: 10}}>
              <UtilsInput
                dataSet={this.userFirstNameInputDataSet ||= {class: "user-first-name-input"}}
                defaultValue={userFirstName}
                label={t(".first_name_of_user")}
                name="school_role[user_attributes][first_name]"
                style={this.userFirstNameInputStyle ||= {marginBottom: 10}}
              />
              <UtilsInput
                dataSet={this.userLastNameInputDataSet ||= {class: "user-last-name-input"}}
                defaultValue={userLastName}
                label={t(".last_name_of_user")}
                name="school_role[user_attributes][last_name]"
                style={this.userLastNameInputStyle ||= {marginBottom: 10}}
              />
              <UtilsInput
                dataSet={this.userEmailInputDataSet ||= {class: "user-email-input"}}
                defaultValue={userEmail}
                label={t(".email_of_user")}
                name="school_role[user_attributes][email]"
                style={this.userEmailInputStyle ||= {marginBottom: 10}}
              />
            </View>
          }
          <UtilsButton
            dataSet={this.saveSchoolRoleButtonDataSet ||= {class: "save-school-role-button"}}
            onPress={this.tt.onSavePressed}
            style={this.saveSchoolRoleButtonStyle ||= {marginTop: 10}}
            title={this.s.createNewUser ? t(".invite_user_and_assign_to_school") : t(".assign_user_to_school")}
          />
        </Form>
      </Modal>
    )
  }

  onSchoolClassesChanged = ({options}) => {
    this.setState({
      schoolClassIds: options.map((option) => option.value)
    })
  }

  onUserChanged = ({options}) => {
    const option = options[0]

    if (option?.optionType == "teacher") {
      this.setState({
        createNewUser: false,
        userId: option.value
      })
    } else if (option?.optionType == "create-new-teacher") {
      this.setState({
        createNewUser: true,
        userId: null
      })
    }
  }

  onSavePressed = async () => {
    const {t} = this.tt

    try {
      const isNewSchoolRole = this.tt.schoolRole.isNewRecord()
      const saveData = this.tt.formRef.current.asObject()

      saveData.school_role.school_id = this.p.school.id()

      if (this.s.userId) {
        saveData.school_role.user_id = this.s.userId
      } else if (this.s.createNewUser) {
        const password = uuidv4()
        const userAttributes = saveData.school_role.user_attributes || {}

        saveData.school_role.user_attributes = {
          ...userAttributes,
          password,
          password_confirmation: password
        }
      } else {
        FlashMessage.alert(t(".you_must_choose_an_existing_user_or_create_a_new_one"))

        return
      }

      await ContactRelationship.schoolLeaderCreate(saveData)
      FlashMessage.success(isNewSchoolRole ? t(".the_role_was_created") : t(".the_role_was_updated"))
      this.p.onRequestClose()
    } catch (e) {
      FlashMessage.errorResponse(e)
    }
  }
}

export default memo(shapeComponent(ComponentsSchoolRolesNewRoleModal))
